import { useMemo } from 'react';
import { DemeterPageElementModel, DemeterPageElementType, DemeterPageType } from '../../../../Generated/Raven-Demeter';
import usePageElementsApi from '../../../Apis/Hooks/usePageElementsApiHook';
import useRegionCommodityMenuSelection from './useRegionCommodityMenuSelectionHook';

interface IPageElementForComponentProps {
    pageType?: DemeterPageType;
    pageElementType?: DemeterPageElementType;
}

const usePageElementForComponent = (props?: IPageElementForComponentProps): DemeterPageElementModel | null => {
    const listPageElements = usePageElementsApi();
    const [getRegionCommoditySelection] = useRegionCommodityMenuSelection();

    const currentPageElement = useMemo(() => {
        if (!props?.pageElementType || !props?.pageType || !listPageElements?.length || !getRegionCommoditySelection) {
            return null;
        }

        const filterPageElements = (looseCommodityCheckCase1: boolean, looseCommodityCheckCase2: boolean) =>
            listPageElements.filter((pageElement) => {
                if (pageElement.pageType !== props.pageType || !pageElement.pageElementType || pageElement.pageElementType !== props.pageElementType) {
                    return false;
                }

                if (getRegionCommoditySelection.region !== pageElement.region) {
                    return false;
                }

                // Null commodity.  Examples: 'Other' or 'Whey'.
                if (!getRegionCommoditySelection.commodity && pageElement.commodity) {
                    return false;
                }

                if (!getRegionCommoditySelection.isSubSelection && (pageElement.subRegion || pageElement.subCommodity)) {
                    return false;
                }

                if ((getRegionCommoditySelection.commodity ?? null) !== (pageElement.commodity ?? null)) {
                    if (!looseCommodityCheckCase1 && !looseCommodityCheckCase2) {
                        return false;
                    }

                    // Loose check case 1: does pageElement commodity contain selection commodity?
                    // Example: "ButterAndAnhydrousMilkFat" contains "Butter"
                    if (
                        looseCommodityCheckCase1 &&
                        !pageElement.commodity?.toLowerCase().includes(getRegionCommoditySelection.commodity?.toLowerCase() ?? '')
                    ) {
                        return false;
                    }

                    // Loose check case 2: does selection commodity contain pageElement commodity?
                    // Example: "NonFatDryMilkAndSkimMilkPowder" contains "SkimMilkPowder"
                    if (
                        looseCommodityCheckCase2 &&
                        !getRegionCommoditySelection.commodity?.toLowerCase().includes(pageElement.commodity?.toLowerCase() ?? '')
                    ) {
                        return false;
                    }
                }

                if (getRegionCommoditySelection.isSubSelection) {
                    if ((getRegionCommoditySelection.subRegion || null) !== (pageElement.subRegion ?? null)) {
                        return false;
                    }

                    if ((getRegionCommoditySelection.subCommodity ?? null) !== (pageElement.subCommodity ?? null)) {
                        return false;
                    }
                }

                return true;
            });
        let filteredPageElements = filterPageElements(false, false);

        if (filteredPageElements.length === 0) {
            filteredPageElements = filterPageElements(true, false);
        }
        if (filteredPageElements.length === 0) {
            filteredPageElements = filterPageElements(false, true);
        }
        filteredPageElements.sort((a, b) => (b.commodity?.length ?? 0) - (a.commodity?.length ?? 0));

        return filteredPageElements.length > 0 ? filteredPageElements[0] : null;
    }, [listPageElements, getRegionCommoditySelection, props?.pageType, props?.pageElementType]);

    return currentPageElement;
};

export default usePageElementForComponent;
